/**
 * Vercel AI SDK adapter for morph-warp-grep tool
 *
 * @example Using with your own Zod instance (recommended for avoiding version conflicts)
 * ```typescript
 * import { z } from 'zod'; // Your project's Zod
 * import { createWarpGrepTool } from '@morphllm/morphsdk/tools/warp-grep/vercel';
 *
 * const schema = z.object({
 *   search_term: z.string().describe('Search problem statement'),
 * });
 *
 * const grepTool = createWarpGrepTool({
 *   repoRoot: '.',
 *   inputSchema: schema,
 * });
 * ```
 */

import { tool } from 'ai';
import { z } from 'zod';
import { jsonSchema } from 'ai';
import { executeToolCall, executeToolCallStreaming, formatResult, WarpGrepClient, executeGitHubReadFile, formatGitHubReadFileResult } from './client.js';
import { WARP_GREP_DESCRIPTION, GITHUB_SEARCH_DESCRIPTION, GITHUB_READ_FILE_DESCRIPTION } from './prompts.js';
import type { WarpGrepToolConfig, WarpGrepResult, GitHubSearchToolConfig, GitHubReadFileInput, GitHubReadFileResult, GitHubReadFileToolConfig } from './types.js';
import type { WarpGrepStep } from './agent/types.js';

/**
 * Raw JSON Schema for warp grep input (no Zod dependency).
 * Use this with jsonSchema() from 'ai' if you have Zod version conflicts.
 *
 * @example
 * ```typescript
 * import { jsonSchema } from 'ai';
 * import { warpGrepJsonSchema, createWarpGrepToolWithSchema } from '@morphllm/morphsdk/tools/warp-grep/vercel';
 *
 * const grepTool = createWarpGrepToolWithSchema({
 *   repoRoot: '.',
 *   inputSchema: jsonSchema(warpGrepJsonSchema),
 * });
 * ```
 */
export const warpGrepJsonSchema = {
  type: 'object' as const,
  properties: {
    search_term: {
      type: 'string' as const,
      description: 'Search problem statement that this subagent is supposed to research for',
    },
  },
  required: ['search_term'] as const,
  additionalProperties: false as const,
};

/** Type for the model-facing warp grep tool input (snake_case to match JSON schema) */
export type WarpGrepToolInput = { search_term: string };

/**
 * Extended config that accepts a custom inputSchema
 */
export interface WarpGrepVercelConfig extends WarpGrepToolConfig {
  /**
   * Custom Zod schema or AI SDK schema. Use this to provide your own Zod instance
   * to avoid version conflicts between your project and the SDK.
   *
   * @example Using your own Zod
   * ```typescript
   * import { z } from 'zod'; // Your Zod version
   *
   * const grepTool = createWarpGrepTool({
   *   repoRoot: '.',
   *   inputSchema: z.object({
   *     search_term: z.string().describe('Search problem statement'),
   *   }),
   * });
   * ```
   *
   * @example Using jsonSchema (no Zod needed)
   * ```typescript
   * import { jsonSchema } from 'ai';
   * import { warpGrepJsonSchema } from '@morphllm/morphsdk/tools/warp-grep/vercel';
   *
   * const grepTool = createWarpGrepTool({
   *   repoRoot: '.',
   *   inputSchema: jsonSchema(warpGrepJsonSchema),
   * });
   * ```
   */
  inputSchema?: Parameters<typeof tool>[0]['inputSchema'];
}

/**
 * Execute warp grep search
 *
 * @param input - Tool input with search_term
 * @param config - Configuration with repoRoot and optional provider
 * @returns Search results
 */
export async function execute(
  input: { search_term: string },
  config: WarpGrepToolConfig
): Promise<WarpGrepResult> {
  return executeToolCall(input, config);
}

// Re-export formatResult for convenience
export { formatResult };

/**
 * Create Vercel AI SDK warp grep tool
 *
 * @param config - Configuration options
 * @returns Vercel AI SDK tool
 *
 * @example Local usage (uses SDK's Zod)
 * ```typescript
 * import { generateText } from 'ai';
 * import { anthropic } from '@ai-sdk/anthropic';
 * import { createWarpGrepTool } from '@morphllm/morphsdk/tools/warp-grep/vercel';
 *
 * const grepTool = createWarpGrepTool({ repoRoot: '.' });
 *
 * const result = await generateText({
 *   model: anthropic('claude-sonnet-4-5-20250929'),
 *   tools: { grep: grepTool },
 *   prompt: 'Find authentication middleware'
 * });
 * ```
 *
 * @example With your own Zod (avoids version conflicts)
 * ```typescript
 * import { z } from 'zod'; // Your project's Zod
 * import { createWarpGrepTool } from '@morphllm/morphsdk/tools/warp-grep/vercel';
 *
 * const grepTool = createWarpGrepTool({
 *   repoRoot: '.',
 *   inputSchema: z.object({
 *     search_term: z.string().describe('Search problem statement'),
 *   }),
 * });
 * ```
 *
 * @example Without Zod (using jsonSchema)
 * ```typescript
 * import { jsonSchema } from 'ai';
 * import { createWarpGrepTool, warpGrepJsonSchema } from '@morphllm/morphsdk/tools/warp-grep/vercel';
 *
 * const grepTool = createWarpGrepTool({
 *   repoRoot: '.',
 *   inputSchema: jsonSchema(warpGrepJsonSchema),
 * });
 * ```
 *
 * @example Remote sandbox (E2B, Modal, etc.)
 * ```typescript
 * const grepTool = createWarpGrepTool({
 *   repoRoot: '/home/repo',
 *   remoteCommands: {
 *     grep: async (pattern, path) => (await sandbox.run(`rg '${pattern}' '${path}'`)).stdout,
 *     read: async (path, start, end) => (await sandbox.run(`sed -n '${start},${end}p' '${path}'`)).stdout,
 *     listDir: async (path, maxDepth) => (await sandbox.run(`find '${path}' -maxdepth ${maxDepth}`)).stdout,
 *   },
 * });
 * ```
 */
export function createWarpGrepTool(config: WarpGrepVercelConfig) {
  // Use provided schema or fall back to SDK's Zod schema
  const schema = config.inputSchema ?? z.object({
    search_term: z.string().describe('Search problem statement that this subagent is supposed to research for'),
  });

  return tool({
    description: config.description ?? WARP_GREP_DESCRIPTION,
    inputSchema: schema,
    execute: async (params: WarpGrepToolInput) => {
      // Use streaming execution to collect intermediate steps
      const steps: WarpGrepStep[] = [];
      const generator = executeToolCallStreaming(params, config);

      let result: WarpGrepResult;
      for (;;) {
        const { value, done } = await generator.next();
        if (done) {
          result = value;
          break;
        }
        steps.push(value);
      }

      if (!result!.success) {
        throw new Error(`Failed to search codebase: ${result!.error}`);
      }

      // Flatten all intermediate tool calls across turns for progress display
      const allToolCalls = steps.flatMap(s => s.toolCalls);
      return {
        success: true,
        contexts: result!.contexts,
        summary: result!.summary,
        progress: allToolCalls.length > 0 ? { turn: steps.length, toolCalls: allToolCalls } : undefined,
      };
    },
  });
}

/**
 * Create a GitHub search tool for Vercel AI SDK
 *
 * @param config - Configuration options (morphApiKey, morphApiUrl, codeSearchUrl, timeout)
 * @returns Vercel AI SDK tool
 *
 * @example
 * ```typescript
 * import { generateText } from 'ai';
 * import { anthropic } from '@ai-sdk/anthropic';
 * import { createGitHubSearchTool } from '@morphllm/morphsdk/tools/warp-grep/vercel';
 *
 * const githubTool = createGitHubSearchTool({ morphApiKey: process.env.MORPH_API_KEY });
 *
 * const result = await generateText({
 *   model: anthropic('claude-sonnet-4-5-20250929'),
 *   tools: { githubSearch: githubTool },
 *   prompt: 'How does auth work in vercel/next.js?'
 * });
 * ```
 */
export function createGitHubSearchTool(config: GitHubSearchToolConfig) {
  const client = new WarpGrepClient(config);

  return tool({
    description: GITHUB_SEARCH_DESCRIPTION,
    inputSchema: z.object({
      search_term: z.string().describe('Search problem statement that this subagent is supposed to research for'),
      github_url: z.string().optional().describe('GitHub repository URL to search (e.g. "https://github.com/vercel/next.js"). You must provide either github_url or owner_repo.'),
      owner_repo: z.string().optional().describe('Repository owner/repo shorthand (e.g. "vercel/next.js"). You must provide either github_url or owner_repo.'),
      branch: z.string().optional().describe('Branch to search (defaults to repo default branch)'),
    }),
    execute: async (params: { search_term: string; github_url?: string; owner_repo?: string; branch?: string }) => {
      const github = params.github_url || params.owner_repo;
      if (!github) {
        throw new Error('Please provide github search url or owner/repo id');
      }
      const result = await client.searchGitHub({ searchTerm: params.search_term, github, branch: params.branch });
      if (!result.success) {
        throw new Error(`GitHub search failed: ${result.error}`);
      }
      return {
        success: true,
        contexts: result.contexts,
        summary: result.summary,
      };
    },
  });
}

/**
 * Create a GitHub read file tool for Vercel AI SDK
 *
 * @param config - Optional configuration (timeout)
 * @returns Vercel AI SDK tool
 *
 * @example
 * ```typescript
 * import { generateText } from 'ai';
 * import { anthropic } from '@ai-sdk/anthropic';
 * import { createGitHubReadFileTool } from '@morphllm/morphsdk/tools/warp-grep/vercel';
 *
 * const readFileTool = createGitHubReadFileTool();
 *
 * const result = await generateText({
 *   model: anthropic('claude-sonnet-4-5-20250929'),
 *   tools: { readFile: readFileTool },
 *   prompt: 'Read the package.json from vercel/next.js'
 * });
 * ```
 */
export function createGitHubReadFileTool(config?: GitHubReadFileToolConfig) {
  return tool({
    description: GITHUB_READ_FILE_DESCRIPTION,
    inputSchema: z.object({
      github: z.string().describe('owner/repo shorthand (e.g., "vercel/next.js")'),
      path: z.string().describe('File path within the repository'),
      startLine: z.number().optional().describe('Start line number (1-based)'),
      endLine: z.number().optional().describe('End line number (1-based, inclusive)'),
      branch: z.string().optional().describe('Branch to read from (defaults to repo default branch)'),
    }),
    execute: async (params: GitHubReadFileInput) => {
      const result = await executeGitHubReadFile(params, config);
      if (!result.success) {
        throw new Error(`GitHub read file failed: ${result.error}`);
      }
      return result;
    },
  });
}

// Default export for convenience
export default createWarpGrepTool;
